import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, CursorTooltip } from '@/ui/primitives';
import { elementOrDescendantHasOverflow } from './overflowTooltipUtils';
import type { SxProps, Theme } from '@/ui/primitives';
import type { CursorTooltipProps } from '@/ui/primitives';
import type { ReactElement, ReactNode } from 'react';

const overflowTooltipSingleLineSx: SxProps<Theme> = {
  fontSize: '15px',
  px: '10px',
  py: '6px',
  whiteSpace: 'nowrap',
  width: 'fit-content',
  maxWidth: 'calc(100vw - 32px)',
};

type OverflowTooltipProps = {
  title: ReactNode | null | undefined;
  children: ReactNode;
  wrapperSx?: SxProps<Theme>;
  tooltipSx?: SxProps<Theme>;
  inline?: boolean;
  placement?: CursorTooltipProps['placement'];
};

export const OverflowTooltip = ({
  title,
  children,
  wrapperSx,
  tooltipSx,
  inline = false,
  placement = 'right-start',
}: OverflowTooltipProps): ReactElement => {
  const containerRef = useRef<HTMLSpanElement | null>(null);
  const [overflowed, setOverflowed] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const resolvedTitle = typeof title === 'string' ? title.trim() : (title ?? null);
  const hasTitle =
    typeof resolvedTitle === 'string' ? resolvedTitle.length > 0 : resolvedTitle !== null;

  const getOverflowState = useCallback((): boolean => {
    const node = containerRef.current;
    if (!node) return false;
    return elementOrDescendantHasOverflow(node);
  }, []);

  const updateOverflowState = useCallback((): boolean => {
    const nextOverflowed = getOverflowState();
    setOverflowed(nextOverflowed);
    return nextOverflowed;
  }, [getOverflowState]);

  const handleOpen = useCallback(() => {
    if (!hasTitle || !updateOverflowState()) {
      setTooltipOpen(false);
      return;
    }

    setTooltipOpen(true);
  }, [hasTitle, updateOverflowState]);

  const handleClose = useCallback(() => {
    setTooltipOpen(false);
  }, []);

  useEffect(() => {
    updateOverflowState();
    const node = containerRef.current;
    if (!node || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      updateOverflowState();
    });
    observer.observe(node);
    const childNode = node.firstElementChild;
    if (childNode instanceof HTMLElement) {
      observer.observe(childNode);
    }
    return () => {
      observer.disconnect();
    };
  }, [resolvedTitle, updateOverflowState]);

  useEffect(() => {
    if (!hasTitle || !overflowed) {
      setTooltipOpen(false);
    }
  }, [hasTitle, overflowed]);

  return (
    <CursorTooltip
      open={tooltipOpen}
      title={resolvedTitle}
      placement={placement}
      arrow
      slotProps={{
        tooltip: {
          sx: [
            overflowTooltipSingleLineSx,
            ...(tooltipSx ? (Array.isArray(tooltipSx) ? tooltipSx : [tooltipSx]) : []),
          ],
        },
      }}
    >
      <Box
        ref={containerRef}
        component="span"
        onMouseEnter={handleOpen}
        onMouseLeave={handleClose}
        onFocus={handleOpen}
        onBlur={handleClose}
        sx={[
          {
            minWidth: 0,
            maxWidth: '100%',
            width: inline ? 'auto' : '100%',
            display: inline ? 'inline-block' : 'block',
          },
          ...(wrapperSx ? (Array.isArray(wrapperSx) ? wrapperSx : [wrapperSx]) : []),
        ]}
      >
        {children}
      </Box>
    </CursorTooltip>
  );
};
